import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';

const EditViewTeam = ({ editable, viewable, onHide, rowRecord, onClose }) => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [allTeamMembers, setAllTeamMembers] = useState([]);
    const [loadingBtn, setLoadingBtn] = useState(false);

    const dispatch = useDispatch();

    const validationSchema = Yup.object().shape({
        teamName: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('Team name is required')),
            // .matches(/^[a-zA-Z0-9\s]*$/, 'Team name must contain only alphanumeric values'),
        teamLead: Yup.mixed().required(t('Team lead is required')),
        members: Yup.array().min(0, t('Select at least one member'))
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            teamName: '',
            teamId: '',
            teamLead: '',
            members: []
        },
        onSubmit: async (data) => {
            setLoadingBtn(true);

            // Compute usersToRemove as: all current team members - currently selected members
            const allMemberIds = (allTeamMembers || []).map((member) => member?._id);
            const selectedMemberIds = data.members || [];
            const usersToRemove = allMemberIds.filter((id) => !selectedMemberIds.includes(id));

            const editData = {
                ...(editable ? {} : { name: data.teamName }), // Only include name when creating, not when editing
                head: data.teamLead,
                usersToRemove
            };

            const response = await dispatch(handlePutRequest(editData, endPoints.updateTeam(data.teamId), true));
            if (response?.status === 200) {
                formik.resetForm();
                onHide();
            }
            setLoadingBtn(false);
        }
    });

    const getTeamMembers = async () => {
        const response = await handleGetRequest(endPoints.getTeamMembersById(rowRecord._id), true, false, dispatch);
        if (response?.data) {
            const members = Array.isArray(response.data) ? response.data : response.data?.members || [];
            const membersWithFullName = members.map((member) => ({
                ...member,
                fullName: `${member?.firstName || ''} ${member?.lastName || ''}`.trim()
            }));
            setAllTeamMembers(membersWithFullName);

            // By default, select all team members (for add, edit, and view)
            const allMemberIds = membersWithFullName.map((member) => member?._id);
            formik.setFieldValue('members', allMemberIds);
        }
    };

    const getTeamById = async () => {
        setLoadingBtn(true);
        const response = await handleGetRequest(endPoints.getTeamById(rowRecord._id), true);
        if (response) {
            formik.setFieldValue('teamId', response?.data?._id);
            formik.setFieldValue('teamName', response?.data?.name);
            const head = response?.data?.head;
            const headId = typeof head === 'string' ? head : head?._id;
            formik.setFieldValue('teamLead', headId || '');
        }
        setLoadingBtn(false);
    };

    useEffect(() => {
        if (editable || viewable) {
            getTeamMembers();

            getTeamById();
        }
    }, [rowRecord]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="grid p-fluid">
                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="teamName">
                            {t('Team Name')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="teamName" name="teamName" placeholder={t('Enter Team Name')} value={formik?.values?.teamName} maxLength={100} onChange={formik.handleChange} disabled={viewable || editable} className="w-full" />
                        {formik.touched.teamName && formik.errors.teamName && <small className="p-error">{formik.errors.teamName}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="teamLead">
                            {t('Team Lead')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="teamLead"
                            name="teamLead"
                            value={formik?.values?.teamLead}
                            options={allTeamMembers}
                            optionLabel="fullName"
                            optionValue="_id"
                            onChange={(e) => {
                                formik.setFieldValue('teamLead', e.value);

                                // Ensure selected team lead is always part of members
                                const currentMembers = formik.values.members || [];
                                if (e.value && !currentMembers.includes(e.value)) {
                                    formik.setFieldValue('members', [...currentMembers, e.value]);
                                }
                            }}
                            placeholder={t('Select Team Lead')}
                            className="w-full"
                            disabled={viewable}
                            filter
                            filterBy="fullName"
                            showClear
                        />
                        {formik.touched.teamLead && formik.errors.teamLead && <small className="p-error">{formik.errors.teamLead}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="members">{t('Team Members')}</label>
                        {viewable ? (
                            <div
                                className="p-2 border-1 border-200 border-round surface-50"
                                style={{ maxHeight: '220px', overflowY: 'auto' }}
                            >
                                {allTeamMembers && allTeamMembers.length > 0 ? (
                                    allTeamMembers.map((member) => {
                                        const isLead = member?._id === formik.values.teamLead;
                                        return (
                                            <div
                                                key={member?._id}
                                                className="flex justify-content-between align-items-center py-1 px-2 border-bottom-1 border-100"
                                            >
                                                <span>{member?.fullName || `${member?.firstName || ''} ${member?.lastName || ''}`.trim()}</span>
                                                {isLead && <span className="text-sm text-500">{t('Lead')}</span>}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <small className="text-500">{t('No team members found.')}</small>
                                )}
                            </div>
                        ) : (
                            <>
                                <MultiSelect
                                    id="members"
                                    name="members"
                                    value={formik?.values?.members}
                                    options={allTeamMembers}
                                    optionLabel="fullName"
                                    optionValue="_id"
                                    onChange={(e) => {
                                        const selected = e.value || [];
                                        const leadId = formik.values.teamLead;

                                        // Prevent removing the team lead from members
                                        const finalSelected = leadId && !selected.includes(leadId) ? [...selected, leadId] : selected;

                                        formik.setFieldValue('members', finalSelected);
                                    }}
                                    placeholder={t('Select Team Members')}
                                    className="w-full"
                                    filter
                                    filterBy="fullName"
                                    display="chip"
                                    showClear
                                    maxSelectedLabels={5}
                                    selectedItemsLabel={t('{0} members selected')}
                                    optionDisabled={(option) => option?._id === formik.values.teamLead}
                                />
                                {formik.touched.members && formik.errors.members && (
                                    <small className="p-error">{formik.errors.members}</small>
                                )}
                                <small className="text-500">
                                    {t('Select multiple users to add them to the team. You can remove members by deselecting them.')}
                                </small>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-content-center mt-4 gap-2">
                {!viewable && (
                    <Button label={editable ? t('Update') : t('Save')} icon={editable ? 'pi pi-pencil' : 'pi pi-save'} type="submit" disabled={loadingBtn} className="p-button-success p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} />
                )}
                <Button label={t('Cancel')} icon="pi pi-times" type="button" className="p-button-secondary p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} onClick={() => onClose()} />
            </div>
        </form>
    );
};

export default EditViewTeam;
